import * as stylex from '@stylexjs/stylex';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ApprovalFlowSelect } from '@/accessControl/ApprovalFlowSelect.molecule';
import { ApprovalFlowStepItem } from '@/accessControl/ApprovalFlowStepItem.molecule';
import { Alert, Modal } from '@seeqdev/qomponents';
import type { WorkbookApprovalFlow } from '@/worksheet/worksheet.types';

type ApprovalFlowModalProps = {
  isApplyingFlow?: boolean;
  /** The flow shown when viewing steps. Ignored when applying, where the user picks from `availableFlows`. */
  approvalFlow: WorkbookApprovalFlow | null;
  /** The active flows offered in the apply dropdown. */
  availableFlows?: WorkbookApprovalFlow[];
  /** The step the item is waiting to be approved into, highlighted in the list. */
  currentStepId?: string;
  itemType: string;
  isApplying: boolean;
  onSubmit: (flow: WorkbookApprovalFlow | null) => Promise<void>;
  onClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
};

export const ApprovalFlowModal = ({
  isApplyingFlow = false,
  approvalFlow,
  availableFlows = [],
  currentStepId,
  itemType,
  isApplying,
  onSubmit,
  onClose,
  onCloseAutoFocus,
}: ApprovalFlowModalProps) => {
  const { t } = useTranslation();
  const [selectedFlowId, setSelectedFlowId] = useState<string>();
  const [isFlowMenuOpen, setIsFlowMenuOpen] = useState(false);
  const selectedFlow = availableFlows.find((flow) => flow.id === selectedFlowId) ?? null;
  const displayedFlow = isApplyingFlow ? selectedFlow : approvalFlow;
  const steps = displayedFlow?.steps ?? [];
  const lockStep = steps.find((step) => step.lockRequired);
  const finalStep = steps[steps.length - 1];

  return (
    <Modal
      open={true}
      onClose={onClose}
      onCloseAutoFocus={onCloseAutoFocus}
      onEscapeKeyDown={(event) => {
        if (!isFlowMenuOpen) {
          return;
        }
        // Radix listens for Escape on the document before the select sees it, and marks the event handled.
        // Closing the menu here keeps that keypress from dismissing the modal.
        event.preventDefault();
        setIsFlowMenuOpen(false);
      }}
      onSubmit={() => onSubmit(displayedFlow)}
      disableSubmitButton={isApplying || (isApplyingFlow && !selectedFlow)}
      submitButtonLabel={isApplyingFlow ? t('APPROVAL_FLOWS.APPLY') : t('DONE')}
      cancelButtonLabel={t('CANCEL')}
      testId="applyApprovalFlowModal"
      title={
        isApplyingFlow
          ? t('APPROVAL_FLOWS.APPLY_TITLE')
          : t('APPROVAL_FLOWS.VIEW_TITLE', { flowName: approvalFlow?.name })
      }>
      <div {...stylex.props(styles.body)} aria-busy={isApplying}>
        {isApplying ? (
          <span role="status" {...stylex.props(styles.applyingStatus)}>
            {t('APPROVAL_FLOWS.APPLYING')}
          </span>
        ) : null}
        {isApplyingFlow ? (
          <ApprovalFlowSelect
            availableFlows={availableFlows}
            selectedFlowId={selectedFlowId}
            onSelectedFlowIdChange={setSelectedFlowId}
            itemType={itemType}
            isDisabled={isApplying}
            menuIsOpen={isFlowMenuOpen}
            onMenuOpen={() => setIsFlowMenuOpen(true)}
            onMenuClose={() => setIsFlowMenuOpen(false)}
          />
        ) : null}
        {isApplyingFlow && lockStep ? (
          <div data-testid="applyApprovalFlowDescription">
            {t('APPROVAL_FLOWS.APPLY_LOCK_DESCRIPTION', { lockStep: lockStep.name, itemType })}
          </div>
        ) : null}
        {displayedFlow ? (
          <div>
            <div id="approval-steps-label" {...stylex.props(styles.stepsHeading)}>
              {t('APPROVAL_FLOWS.STEPS')}
            </div>
            <ol aria-labelledby="approval-steps-label" {...stylex.props(styles.stepList)}>
              {steps.map((step) => (
                <ApprovalFlowStepItem
                  key={step.id}
                  step={step}
                  isCurrentStep={step.id === currentStepId}
                  itemType={itemType}
                />
              ))}
            </ol>
          </div>
        ) : null}
        {isApplyingFlow && finalStep ? (
          <Alert
            variant="warning"
            dismissible={false}
            testId="applyApprovalFlowProductionImpact"
            xstyle={styles.productionImpact}>
            <span {...stylex.props(styles.productionImpactText)}>
              <Trans
                i18nKey="APPROVAL_FLOWS.APPLY_FINAL_STEP_PRODUCTION_DESCRIPTION"
                values={{
                  stepName: finalStep.name,
                  itemType,
                }}
                components={{ Status: <em /> }}
              />
            </span>
          </Alert>
        ) : null}
      </div>
    </Modal>
  );
};

const styles = stylex.create({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  applyingStatus: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
  stepsHeading: {
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: 'inherit',
  },
  stepList: {
    marginTop: '0.25rem',
    marginBottom: '0.25rem',
    listStyleType: 'decimal',
    gap: '0.25rem',
    paddingLeft: '1.25rem',
  },
  productionImpact: {
    alignItems: 'flex-start',
  },
  productionImpactText: {
    width: '100%',
  },
});
